import React, { useState, useLayoutEffect, useRef, useEffect } from 'react';
import logo from '../../images/logo.png';
import { NavLink, Link, Outlet, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/DashboardSidebarCollapse.css';
import '../css/UserDashboardPrototype.css';
import '../css/Dashboard.css';
import {
  faUsers,
  faUserPlus,
  faGraduationCap,
  faPeopleRoof,
  faHouseMedicalFlag,
  faBell,
  faCakeCandles,
  faMagnifyingGlass,
  faAnglesLeft,
  faDownload,
  faFlag,
  faBars,
  faTimes,
  faSignOutAlt,
} from '@fortawesome/free-solid-svg-icons';

const SIDEBAR_COLLAPSED_KEY = 'dashboardSidebarCollapsed';

const SlashToggle = () => {
  const navigate = useNavigate();
  const isEms = window.location.pathname.startsWith('/ems');
  return (
    <div className="admin-slash-toggle" style={{ fontSize: '18px', cursor: 'pointer' }}>
      <span
        onClick={() => navigate('/')}
        style={{
          fontWeight: isEms ? 'normal' : 'bold',
          color: isEms ? '#888' : '#000',
          marginRight: 5,
        }}
      >
        CRM
      </span>
      |
      <span
        onClick={() => navigate('/ems')}
        style={{
          fontWeight: isEms ? 'bold' : 'normal',
          color: isEms ? '#000' : '#888',
          marginLeft: 5,
        }}
      >
        EMS
      </span>
    </div>
  );
};

const DashboardLayout = ({ handleLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const un = localStorage.getItem('username');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(() => {
    try { return localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true'; } catch { return false; }
  });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const navRef = useRef(null);
  const scrollPos = useRef(0);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => {
      const next = !prev;
      try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch (_) {}
      return next;
    });
  };

  const closeMobileNav = () => setMobileNavOpen(false);

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (nav) nav.scrollTop = scrollPos.current;
    return () => {
      if (nav) scrollPos.current = nav.scrollTop;
    };
  }, [location.pathname]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) navigate('/login');
  }, [navigate]);

  useEffect(() => {
    const onResize = () => {
      if (window.innerWidth >= 992) setMobileNavOpen(false);
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    if (!mobileNavOpen) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [mobileNavOpen]);

  const navItems = [
    { path: '/individuals', label: 'Individuals', icon: faUsers },
    { path: '/add-person', label: 'Add Individual', icon: faUserPlus },
    { path: '/list-roles', label: 'Roles', icon: faGraduationCap },
    { path: '/organizations', label: 'Organisations', icon: faPeopleRoof },
    { path: '/committees', label: 'Committees', icon: faPeopleRoof },
    { path: '/delivery-partners', label: 'Delivery Partners', icon: faPeopleRoof },
    { path: '/status-los-angeles-2028', label: 'Status Los Angeles 2028', icon: faFlag },
    { path: '/organisation', label: 'Add Organisation', icon: faHouseMedicalFlag },
    { path: '/crm-changelog', label: 'Changelogs', icon: faBell },
    { path: '/advanced-search', label: 'Search', icon: faMagnifyingGlass },
    { path: '/birthdays', label: 'Birthdays', icon: faCakeCandles },
    { path: '/downloads', label: 'Downloads', icon: faDownload },
  ];

  return (
    <div
      className={[
        'd-flex user-dashboard-shell',
        sidebarCollapsed ? 'user-dashboard-shell--collapsed' : '',
        mobileNavOpen ? 'user-dashboard-shell--nav-open' : '',
      ]
        .filter(Boolean)
        .join(' ')}
    >
      <button
        type="button"
        className="user-sidebar-backdrop"
        aria-label="Close menu"
        tabIndex={mobileNavOpen ? 0 : -1}
        onClick={closeMobileNav}
      />

      <div
        className={`user-sidebar-container d-flex flex-column ${sidebarCollapsed ? 'collapsed' : ''}`}
        id="crm-sidebar"
      >
        <div className="user-logo-container d-flex align-items-center justify-content-center">
          <img src={logo} alt="CRM Logo" className="user-logo" />
          <span className="user-logo-monogram" aria-hidden="true">IGF</span>
          <button
            type="button"
            className="user-sidebar-close"
            aria-label="Close menu"
            onClick={closeMobileNav}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="sidebar flex-grow-1">
          <ul className="nav flex-column px-0" ref={navRef}>
            {navItems.map((item) => (
              <li className="nav-item" key={item.path}>
                <NavLink
                  to={item.path}
                  className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
                  title={item.label}
                  onClick={closeMobileNav}
                >
                  <span className="nav-icon-circle">
                    <FontAwesomeIcon icon={item.icon} />
                  </span>
                  <span className="sidebar-label">{item.label}</span>
                </NavLink>
              </li>
            ))}
          </ul>
          <div className="sidebar-footer">
            <button
              type="button"
              className="sidebar-collapse-row"
              onClick={toggleSidebar}
              title={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              aria-label={sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
            >
              <span className="sidebar-collapse-icon">
                <FontAwesomeIcon icon={faAnglesLeft} />
              </span>
              <span className="sidebar-footer-label">
                {sidebarCollapsed ? 'Expand menu' : 'Collapse menu'}
              </span>
            </button>
          </div>
        </div>
      </div>

      <div className="content d-flex flex-column flex-grow-1">
        <nav className="navbar navbar-light user-topbar">
          <button
            type="button"
            className="user-topbar__menu-btn"
            aria-label="Open menu"
            aria-expanded={mobileNavOpen}
            aria-controls="crm-sidebar"
            onClick={() => setMobileNavOpen(true)}
          >
            <FontAwesomeIcon icon={faBars} />
          </button>

          <div className="user-topbar__leading">
            <SlashToggle />
          </div>

          <div className="user-topbar__banner" />

          <h4 className="navbar-brand mb-0 user-topbar__welcome">
            <div className="dropdown">
              <button
                className="btn btn-light dropdown-toggle"
                id="userDropdown"
                data-bs-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
              >
                Welcome, {un || 'Guest'}
              </button>
              <ul className="dropdown-menu dropdown-menu-end" aria-labelledby="userDropdown">
                <li>
                  <Link className="dropdown-item" to="/profile">Profile</Link>
                </li>
                <li>
                  <Link className="dropdown-item" to="/change-password">Change Password</Link>
                </li>
                <li>
                  <button className="dropdown-item" onClick={handleLogout}>
                    <FontAwesomeIcon icon={faSignOutAlt} className="me-2" />
                    Logout
                  </button>
                </li>
              </ul>
            </div>
          </h4>
        </nav>

        <div className="d-flex flex-grow-1 user-dashboard-body">
          <div className="content-full-width admin-dashboard-content">
            <Outlet />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardLayout;
